import { FormikProps } from "formik";
import { useSelector } from "react-redux";
import { RootState } from "../../../../reducers";
import { OwcListItem, OwcSelectDropdown, OwcSlideToggle } from "@one/react";
import { KEEP_STATUS_OPTION, KEEP_STATUS_VALUE } from "../../../../logs/StepperContainer";
import ACTION_LOGS_DATA_MODEL from "../../../../utils/constants/actionLogsDataModel";
import { react18WorkaroundProps } from "../../../../utils/constants/react18WorkaroundProps";
import { useFieldPropagationToggle } from "../../../../utils/hooks/useFieldPropagationToggle";

export type GxpReadyValue = {
  value: string;
  key: string;
  newValue: { value: string; key: string };
  shouldBePublished: boolean;
};

export const QualificationStatusSelectWithSwitch = ({
  formik,
  editMode,
  cluster = false,
  isPropagationDisabled,
  numberOfSelectedEquipment,
  defaultValue
}: {
  formik: FormikProps<{
    gxpReady: GxpReadyValue;
    gxpReadyCluster?: GxpReadyValue;
  }>;
  editMode: boolean;
  cluster?: boolean;
  isPropagationDisabled: boolean;
  numberOfSelectedEquipment: number;
  defaultValue: string;
}) => {
  const gxpReadys = useSelector((store: RootState) => store.runLogsForm.gxpReadys) as { key: string; value: string }[];
  const selectedValue = cluster ? "gxpReadyCluster" : "gxpReady";
  const formikObject = formik.values[selectedValue];

  // Adjust default value to "Keep current status" if multiple equipment are selected
  const adjustedDefaultValue = numberOfSelectedEquipment > 1 ? KEEP_STATUS_VALUE.value : (gxpReadys.find((item) => item.key === defaultValue)?.value || '');

  const { isToggleDisabled } = useFieldPropagationToggle({
    formikValue: formikObject ?? { newValue: '', shouldBePublished: false },
    defaultValue: adjustedDefaultValue,
    label: selectedValue,
    setFieldValue: formik.setFieldValue,
    disabled: editMode || isPropagationDisabled
  });

  const options = [
    ...(numberOfSelectedEquipment > 1 ? [KEEP_STATUS_OPTION] : []),
    ...gxpReadys.map(({ key, value }) => ({
      key,
      value,
      label: numberOfSelectedEquipment > 1 ? `Set all to "${value}"` : value
    }))
  ] as { key: string; value: string; label: string }[];

  return (
    <>
      <OwcSelectDropdown
        style={{ width: "100%" }}
        label={`${ACTION_LOGS_DATA_MODEL.gxpReady.value} *`}
        required
        {...react18WorkaroundProps}
        onSelectChange={(event: CustomEvent) =>
          formik.setFieldValue(`${selectedValue}.newValue`, {
            key: options[event.detail[0]].key,
            value: options[event.detail[0]].value
          })
        }
      >
        {options.map((option) => {
          return (
            <OwcListItem
              {...react18WorkaroundProps}
              key={option.key}
              selected={option.key === (formikObject?.newValue?.key || formikObject?.key)}
            >
              {option.label}
            </OwcListItem>
          );
        })}
      </OwcSelectDropdown>
      <OwcSlideToggle
        {...react18WorkaroundProps}
        checked={formikObject?.shouldBePublished}
        onInputChange={(event) => formik.setFieldValue(`${selectedValue}.shouldBePublished`, event.detail)}
        id={`${selectedValue}.shouldBePublished`}
        disabled={editMode || isPropagationDisabled || isToggleDisabled}
      >
        {`Publish “${ACTION_LOGS_DATA_MODEL.gxpReady.value}” to Equipment Repository`}
      </OwcSlideToggle>
    </>
  );
};
